import IncomingTable from "../../components/account-distribution/incoming/accIncomingTable.jsx";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useAccDistIncoming } from "../../api/account/useAccDistIncoming";
import GetLeadStatusData from "../../components/lead-inventory/all/getLeadStatus";
import { AllTableSkeleton,LeadInventoryPaginationSkeleton } from "../../components/lead-inventory/skeleton/leadInventorySkeleton";
import Filters from "../../components/lead-inventory/all/filterComponent";
import LeadInventoryPagination from "../../components/lead-inventory/all/leadInventoryTablePagination";
import LeadTableSearchbar from "../../components/lead-inventory/all/leadInventoryTableSearchbar";
import PipelineStageButtons from "../../components/lead-inventory/all/pipelineStageButtons";
import { useHunterFarmerList } from "../../api/auth/useHunterFarmerList";

export default function AllLeadInventoryPage() {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [filters, setFilters] = useState(null);
    const [searchName, setSearchName] = useState("");
    const [selectedPipelineStage, setSelectedPipelineStage] = useState(null);
    const [farmers, setFarmers] = useState([]);

    const { hunterListAPI, farmerListAPI } = useHunterFarmerList();
    useEffect(() => {
        setFarmers(farmerListAPI);
    }, [farmerListAPI]);

    // Get data with refresh function
    const { data, totalPages, totalLeads, leadStatusCount, isLoading, absoluteTotal, pipelineStageCount, refresh } = useAccDistIncoming(page, pageSize, selectedStatus, filters, searchName, selectedPipelineStage);
    
    const handleStatusChange = useCallback((status) => {
        setSelectedStatus(status);
        setPage(1);
    }, []);

    const handlePipelineStageChange = useCallback((stage) => {
        setSelectedPipelineStage(stage);
        setPage(1);
    }, []);

    const handleSearch = useCallback((name) => {
        setSearchName(name);
        setPage(1);
    }, []);

    const productsOptions = useMemo(() => [
        { id: 1, label: 'Remittance' },
        { id: 2, label: 'Insurance' },
        { id: 3, label: 'Re-KYC' },
        { id: 4, label: 'Referral' },
        { id: 5, label: 'Financing' },
    ], []);

    const pipelineStage = useMemo(() => [
        { id: 1, label: 0 }, // Raw
        { id: 2, label: 1 }, // Fresh
        { id: 3, label: 2 }, // Profile Completed
        { id: 4, label: 3 }, // Information Gathered
        { id: 5, label: 4 }, // Onboarded
        { id: 6, label: 5 } // Activated
    ], []);

    const leadStatus = useMemo(() => [
        { id: 0, label: 'Active' },
        { id: 1, label: 'Ongoing' },
        { id: 2, label: 'Inactive' },
        { id: 3, label: 'Tanked' }
    ], []);

    const leadSource = useMemo(() => [
        { id: 1, label:"Self-Generated" },
        { id: 2, label:"Referral (Hunter)" },
        { id: 3, label:"Referral (MM Employee)" },
        { id: 4, label:"Referral (Public)" },
        { id: 5, label:"Inbound" },
        { id: 6, label:"Boost" },
        { id: 7, label:"Referral (Farmer)" },
        { id: 8, label:"Webscraping" },
        { id: 9, label:"Lazada" },
        { id: 10, label:"Bytedance" },
        { id: 11, label:"Referral Scheme" }
    ], []);

    const interestLevel = useMemo(() => [
        { id: 1, label: 1 }, // Cold
        { id: 2, label: 2 }, // Warm
        { id: 3, label: 3 }  // Hot
    ], []);

    const region = useMemo(() => [
        { id: 1, label: 'MY' },
        { id: 2, label: 'SG' },
        { id: 3, label: 'BN' },
        { id: 4, label: 'AU' }
    ], []);

    const handleFilterChange = useCallback((filters) => {
        const selectedFilters = {
            products: filters.products.map(id => 
                productsOptions.find(option => option.id === id)?.label
            ).filter(Boolean),
            pipelineStage: filters.pipelineStage.map(id =>
                pipelineStage.find(option => option.id === id)?.label
            ).filter(Boolean),
            leadStatus: filters.leadStatus.map(id =>
                leadStatus.find(option => option.id === id)?.label
            ).filter(Boolean),
            leadSource: filters.leadSource.map(id =>
                leadSource.find(option => option.id === id)?.label
            ).filter(Boolean),
            interestLevel: filters.interestLevel.map(id =>
                interestLevel.find(option => option.id === id)?.label
            ).filter(Boolean),
            region: filters.region.map(id =>
                region.find(option => option.id === id)?.label
            ).filter(Boolean)
        };
        
        setPage(1);
        setFilters(selectedFilters);
    }, [productsOptions, pipelineStage, leadStatus, leadSource, interestLevel, region]);
    
    return(
        <div className="min-h-screen py-2 px-6">
            <div className="justify-between py-4">
                <Filters onFilterChange={handleFilterChange} />
            </div>
            <GetLeadStatusData
                onStatusChange={handleStatusChange}
                selectedStatus={selectedStatus}
                totalLeads={totalLeads}
                leadStatusCount={leadStatusCount}
            />
            <div className="max-auto mx-auto bg-white border-t rounded-t-lg shadow-[2px_0_8px_rgba(0,0,0,0.1)]">
                <div className="p-3 flex flex-row items-center justify-between">
                    <LeadTableSearchbar 
                        onSearch={handleSearch}
                    />
                    <PipelineStageButtons
                        onPipelineChange={handlePipelineStageChange}
                        selectedPipelineStage={selectedPipelineStage}
                        pipelineStageCount={pipelineStageCount}
                    />
                </div>
            </div>
            {isLoading ? (
                <>
                    <div className="max-auto mx-auto bg-white shadow-md rounded-b-lg">
                        <LeadInventoryPaginationSkeleton/>
                        <AllTableSkeleton/>
                    </div>
                </>
            ) : (
                <>
                    <div className="max-auto mx-auto bg-white shadow-md rounded-b-lg ">
                        {/* Pagination */}
                        <LeadInventoryPagination
                            page={page}
                            setPage={setPage}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            totalLeads={totalLeads}
                            absoluteTotal={absoluteTotal}
                            totalPages={totalPages}
                        />

                        {/* Table - Pass refresh function */}
                        <IncomingTable 
                            data={data} 
                            totalPages={totalPages}
                            farmers={farmers}
                            onRefresh={refresh}
                        />
                    </div>
                </>
            )}
        </div>
    )
}